import { ClinicalRecord, HistoryRecord, InsulinScheduleView } from '../types/domain';

export type ReminderKind = 'insulin' | 'glucose' | 'hba1c' | 'visit';

/** 'due' needs doing now; 'soon' is a heads-up; 'done' is already satisfied. */
export type ReminderStatus = 'due' | 'soon' | 'done';

export interface Reminder {
  kind: ReminderKind;
  status: ReminderStatus;
  title: string;
  detail: string;
}

/** HbA1c is tested roughly quarterly (SoW Bagian 6). */
export const HBA1C_INTERVAL_DAYS = 90;
/** Flag it a fortnight early, so a clinic visit can actually be arranged. */
export const HBA1C_SOON_DAYS = 14;
/** A visit within this many days shows as "soon" (FR-20's visit reminder). */
export const VISIT_SOON_DAYS = 7;

// HBA1C_INTERVAL_DAYS and HBA1C_SOON_DAYS are shared with the push rules in
// due_push_reminders() (migration 016); tests/pushReminders.test.ts pins them.

const dayDiff = (from: string, to: string): number | null => {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return null;
  }
  return Math.floor((b.getTime() - a.getTime()) / 86400000);
};

/**
 * Which configured insulin slots have not been logged today.
 *
 * Matched on the slot, not the clock: the app records what was injected, not
 * when it was due, and a late dose is still a dose taken.
 */
export function pendingInsulinSlots(
  schedule: InsulinScheduleView[],
  todayInjections: HistoryRecord[]
): InsulinScheduleView[] {
  const done = new Set(
    todayInjections.filter(r => r.insulinDose).map(r => r.insulinTypeId)
  );
  return schedule.filter(slot => slot.active && !done.has(slot.slot));
}

/**
 * The in-app reminder cards (FR-13).
 *
 * All derived from data the dashboard already loads — no scheduler, no push.
 * These are the *reliable* delivery path across platforms; FR-20's push is
 * supplementary (C2), and a PWA cannot be relied on to receive it. The push
 * rules live in SQL (migration 016) because they run with the app closed.
 */
export function buildReminders(input: {
  today: string;
  schedule: InsulinScheduleView[];
  todayInjections: HistoryRecord[];
  todayGlucoseCount: number;
  clinicalRecords: ClinicalRecord[];
  /** FR-20. The patient's next clinic visit, if they have set one. */
  nextControlDate?: string | null;
}): Reminder[] {
  const { today, schedule, todayInjections, todayGlucoseCount, clinicalRecords } = input;
  const reminders: Reminder[] = [];

  // --- insulin -------------------------------------------------------------
  if (schedule.length > 0) {
    const pending = pendingInsulinSlots(schedule, todayInjections);
    reminders.push(
      pending.length === 0
        ? {
            kind: 'insulin',
            status: 'done',
            title: 'Suntikan hari ini lengkap',
            detail: `${schedule.length} jadwal sudah dicatat.`
          }
        : {
            kind: 'insulin',
            status: 'due',
            title: 'Belum suntik insulin',
            detail: `Belum dicatat: ${pending.map(s => s.slotLabel).join(', ')}.`
          }
    );
  }

  // --- glucose -------------------------------------------------------------
  reminders.push(
    todayGlucoseCount > 0
      ? {
          kind: 'glucose',
          status: 'done',
          title: 'Sudah cek gula darah',
          detail: `${todayGlucoseCount} kali hari ini.`
        }
      : {
          kind: 'glucose',
          status: 'due',
          title: 'Belum cek gula darah',
          detail: 'Catat hasil cek gula darah hari ini.'
        }
  );

  // --- HbA1c ---------------------------------------------------------------
  const lastHba1c = clinicalRecords
    .filter(r => r.hba1c !== null)
    .map(r => r.recordedAt)
    .sort()
    .pop();

  if (!lastHba1c) {
    reminders.push({
      kind: 'hba1c',
      status: 'due',
      title: 'Belum ada hasil HbA1c',
      detail: 'Catat hasil HbA1c dari pemeriksaan terakhir.'
    });
  } else {
    const since = dayDiff(lastHba1c, today) ?? 0;
    const remaining = HBA1C_INTERVAL_DAYS - since;
    if (remaining <= 0) {
      reminders.push({
        kind: 'hba1c',
        status: 'due',
        title: 'Saatnya cek HbA1c',
        detail: `Terakhir ${lastHba1c}, sudah ${since} hari lalu.`
      });
    } else if (remaining <= HBA1C_SOON_DAYS) {
      reminders.push({
        kind: 'hba1c',
        status: 'soon',
        title: 'Cek HbA1c sebentar lagi',
        detail: `Jadwal berikutnya sekitar ${remaining} hari lagi.`
      });
    } else {
      reminders.push({
        kind: 'hba1c',
        status: 'done',
        title: 'HbA1c terjadwal',
        detail: `Terakhir ${lastHba1c}. Cek lagi sekitar ${remaining} hari lagi.`
      });
    }
  }

  // --- control visit (FR-20) ------------------------------------------------
  // No date set means no card: nagging a family to invent a visit date helps
  // nobody. A date in the past is stale and is dropped the same way.
  const untilVisit = input.nextControlDate ? dayDiff(today, input.nextControlDate) : null;
  if (untilVisit !== null && untilVisit >= 0) {
    reminders.push(
      untilVisit === 0
        ? {
            kind: 'visit',
            status: 'due',
            title: 'Hari ini jadwal kontrol',
            detail: 'Bawa buku catatan dan hasil lab ya.'
          }
        : untilVisit <= VISIT_SOON_DAYS
          ? {
              kind: 'visit',
              status: 'soon',
              title: untilVisit === 1 ? 'Besok jadwal kontrol' : `Kontrol ${untilVisit} hari lagi`,
              detail: `Tanggal ${input.nextControlDate}.`
            }
          : {
              kind: 'visit',
              status: 'done',
              title: 'Kontrol terjadwal',
              detail: `Kontrol berikutnya ${input.nextControlDate}.`
            }
    );
  }

  // Things still to do first — a dashboard that buries the outstanding item
  // under three ticks is not a reminder.
  const order: Record<ReminderStatus, number> = { due: 0, soon: 1, done: 2 };
  return reminders.sort((a, b) => order[a.status] - order[b.status]);
}

/** Today's glucose readings, summarised for the dashboard. */
export function summariseTodayGlucose(todayRecords: HistoryRecord[]): {
  count: number;
  latest: number | null;
  average: number | null;
} {
  const values = todayRecords
    .filter(r => typeof r.glucoseValue === 'number')
    .map(r => r.glucoseValue as number);

  if (values.length === 0) {
    return { count: 0, latest: null, average: null };
  }
  // getHistory returns newest first, so the head is the latest reading.
  return {
    count: values.length,
    latest: values[0],
    average: Math.round(values.reduce((a, b) => a + b, 0) / values.length)
  };
}
